/**
 * Storage Manager - Local data persistence
 */

export class StorageManager {
  constructor(type = 'localStorage') {
    this.type = type;
    this.storage = null;
    this.memoryStore = new Map();
  }

  /**
   * Initialize storage
   */
  async initialize() {
    switch (this.type) {
      case 'localStorage':
        this.storage = window.localStorage;
        break;
      case 'indexedDB':
        this.storage = await this._initIndexedDB();
        break;
      case 'memory':
        this.storage = null;
        break;
      default:
        this.storage = window.localStorage;
    }
  }

  /**
   * Get item
   */
  async get(key) {
    try {
      if (this.type === 'indexedDB') {
        return await this._getFromIndexedDB(key);
      }

      const value = this.storage ? this.storage.getItem(key) : this.memoryStore.get(key);
      if (value === null) return null;

      try {
        return JSON.parse(value);
      } catch {
        return value;
      }
    } catch (error) {
      console.error('Storage get error:', error);
      return null;
    }
  }

  /**
   * Set item
   */
  async set(key, value) {
    try {
      const serialized = typeof value === 'string' ? value : JSON.stringify(value);

      if (this.type === 'indexedDB') {
        return await this._setToIndexedDB(key, value);
      }

      if (this.storage) {
        this.storage.setItem(key, serialized);
      } else {
        this.memoryStore.set(key, serialized);
      }
    } catch (error) {
      console.error('Storage set error:', error);
      throw error;
    }
  }

  /**
   * Remove item
   */
  async remove(key) {
    try {
      if (this.type === 'indexedDB') {
        return await this._removeFromIndexedDB(key);
      }

      if (this.storage) {
        this.storage.removeItem(key);
      } else {
        this.memoryStore.delete(key);
      }
    } catch (error) {
      console.error('Storage remove error:', error);
    }
  }

  /**
   * Clear all data
   */
  async clear() {
    try {
      if (this.type === 'indexedDB') {
        // Clear IndexedDB
        const db = await this._initIndexedDB();
        const transaction = db.transaction(['store'], 'readwrite');
        const store = transaction.objectStore('store');
        await store.clear();
      } else if (this.storage) {
        this.storage.clear();
      } else {
        this.memoryStore.clear();
      }
    } catch (error) {
      console.error('Storage clear error:', error);
    }
  }

  /**
   * Get all keys
   */
  async keys() {
    if (this.type === 'indexedDB') {
      const db = await this._initIndexedDB();
      const transaction = db.transaction(['store'], 'readonly');
      const store = transaction.objectStore('store');
      return await store.getAllKeys();
    }

    if (this.storage) {
      return Object.keys(this.storage);
    }

    return Array.from(this.memoryStore.keys());
  }

  _initIndexedDB() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open('ImToIM_DB', 1);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve(request.result);

      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        if (!db.objectStoreNames.contains('store')) {
          db.createObjectStore('store', { keyPath: 'key' });
        }
      };
    });
  }

  _getFromIndexedDB(key) {
    return new Promise(async (resolve, reject) => {
      const db = await this._initIndexedDB();
      const transaction = db.transaction(['store'], 'readonly');
      const store = transaction.objectStore('store');
      const request = store.get(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.value : null);
      };
    });
  }

  _setToIndexedDB(key, value) {
    return new Promise(async (resolve, reject) => {
      const db = await this._initIndexedDB();
      const transaction = db.transaction(['store'], 'readwrite');
      const store = transaction.objectStore('store');
      const request = store.put({ key, value });

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }

  _removeFromIndexedDB(key) {
    return new Promise(async (resolve, reject) => {
      const db = await this._initIndexedDB();
      const transaction = db.transaction(['store'], 'readwrite');
      const store = transaction.objectStore('store');
      const request = store.delete(key);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => resolve();
    });
  }
}
